<template>
  <div class="project-card">
    <div class="project-image">
      <img :src="projectImage" :alt="project.title" />
    </div>
    <div class="project-content">
      <h3 class="project-title">{{ project.title }}</h3>
      <p class="project-description">{{ project.description }}</p>
      <div class="project-tags">
        <span v-for="(tag, index) in project.tags" :key="index" class="tag">{{ tag }}</span>
      </div>
      
      <div v-if="project.coauthors && project.coauthors.length" class="project-coauthors">
        <h4 class="coauthors-title">{{ $t('projects.coauthors') }}:</h4>
        <div v-for="(coauthor, index) in project.coauthors" :key="index" class="coauthor-item">
          <span class="coauthor-name">{{ coauthor.name }}</span>
          <div class="coauthor-links">
            <a :href="coauthor.github" target="_blank" rel="noopener noreferrer" class="coauthor-link github-link" :title="$t('projects.githubProfile')">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
              </svg>
            </a>
            <a :href="coauthor.linkedin" target="_blank" rel="noopener noreferrer" class="coauthor-link linkedin-link" :title="$t('projects.linkedinProfile')">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                <rect x="2" y="9" width="4" height="12"></rect>
                <circle cx="4" cy="4" r="2"></circle>
              </svg>
            </a>
          </div>
        </div>
      </div>
      
      <RouterLink :to="`/projects/${project.id}`" class="project-link">
        {{ $t('projects.viewProject') }}
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="5" y1="12" x2="19" y2="12"></line>
          <polyline points="12 5 19 12 12 19"></polyline>
        </svg>
      </RouterLink>
    </div>
  </div>
</template>

<script>
import { RouterLink } from 'vue-router'

export default {
  name: 'ProjectCard',
  components: {
    RouterLink
  },
  props: {
    project: {
      type: Object,
      required: true
    }
  },
  computed: {
    projectImage() {
      return this.project.image || this.getDefaultProjectImage();
    }
  },
  methods: {
    getDefaultProjectImage() {
      // Use the first project image from projects folder as default fallback
      return '/images/projects/llm-chess-website.png';
    }
  }
}
</script>

<style lang="scss" scoped>
.project-card {
  background-color: var(--color-card-bg);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 20px var(--color-shadow);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 15px 30px var(--color-shadow);
  }
}

.project-image {
  height: 220px;
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 30px;
    background: linear-gradient(to top, var(--color-card-bg), transparent);
    z-index: 1;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.8s ease;
  }
  
  &:hover img {
    transform: scale(1.08);
  }
}

.project-content {
  padding: 1.8rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.project-title {
  margin-bottom: 0.75rem;
  font-size: 1.35rem;
  color: var(--color-primary);
}

.project-description {
  margin-bottom: 1.2rem;
  color: var(--color-text);
  opacity: 0.95;
  flex-grow: 1;
  line-height: 1.6;
}

.project-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
}

.tag {
  background-color: rgba(var(--color-primary-rgb, 248, 198, 48), 0.1);
  color: var(--color-primary);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.project-coauthors {
  margin-bottom: 1.25rem;
}

.coauthors-title {
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  color: var(--color-text);
  font-weight: 600;
}

.coauthor-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0;
  border-bottom: 1px dashed rgba(var(--color-border-rgb, 0, 0, 0), 0.1);
  
  &:last-child {
    border-bottom: none;
  }
}

.coauthor-name {
  font-size: 0.85rem;
  color: var(--color-text);
}

.coauthor-links {
  display: flex;
  gap: 0.5rem;
}

.coauthor-link {
  color: var(--color-text);
  opacity: 0.7;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 1;
  }
  
  &.github-link:hover {
    color: #333;
  }
  
  &.linkedin-link:hover {
    color: #0077b5;
  }
}

.project-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-primary);
  font-weight: 500;
  transition: all 0.3s ease;
  margin-top: auto;
  
  svg {
    transition: transform 0.3s ease;
  }
  
  &:hover {
    color: var(--color-secondary);
    
    svg {
      transform: translateX(5px);
    }
  }
}
</style>